import { Alert } from "@mui/material"
import { VideoPlatform } from "app-constants/constants"
import { useConventionRegistration } from "contexts/ConventionRegistrationContext"
import useSearchParams from "hooks/useSearchParams"
import { api } from "lib/api"
import openWindowAsync from "lib/open-window-async"
import ConventionSession from "models/convention-api/v1/ConventionSession"
import { useSnackbar } from "notistack"
import { useEffect, useState } from "react"
import useAsyncEffect from "use-async-effect"
import { useBoolean } from "usehooks-ts"

const useJoinSession = (session: ConventionSession) => {
  const [isJoined, setIsJoined] = useState(false)
  const conventionRegistration = useConventionRegistration()
  const snackbar = useSnackbar()
  const searchParams = useSearchParams()
  const sessionIdToJoin = searchParams.get("sessionIdToJoin")
  const joining = useBoolean(false)

  useEffect(() => {
    setIsJoined(false)
  }, [session.id])

  const handleWhereby = async () => {
    if (!session.integrated_meeting_url || !session.whereby_room_created) {
      const message = "Not available, please try again in 1 minute. (Also refresh the page)"
      snackbar.enqueueSnackbar(message, {
        autoHideDuration: 6000,
        content: (key) => (
          <Alert onClose={() => snackbar.closeSnackbar(key)} severity="info">
            {message}
          </Alert>
        ),
      })
      return
    }

    await api.joinSession({ req: undefined }, session.id)
    setIsJoined(true)
  }

  const handleZoom = () => {
    openWindowAsync(async () => {
      await api.joinSession({ req: undefined }, session.id)
      setIsJoined(true)
      return session.integrated_meeting_url
    })
  }

  useAsyncEffect(async () => {
    if (!sessionIdToJoin) return

    if (!conventionRegistration.isRegistered) {
      await conventionRegistration.openRegisterDialog()
      joining.setFalse()
      return
    }

    await searchParams.delete("sessionIdToJoin")
    joining.setFalse()
    if (session.video_platform === VideoPlatform.WHEREBY) return handleWhereby()
    if (session.video_platform === VideoPlatform.ZOOM) return handleZoom()

    // if the session.video_platform is null, then it means it's a zoom call
    return handleZoom()
  }, [
    joining.value,
    sessionIdToJoin,
    conventionRegistration.isRegistered,
    conventionRegistration.state,
  ])

  const join = async () => {
    joining.setTrue()
    await searchParams.set("sessionIdToJoin", session.id)
  }

  return {
    join,
    isJoined,
  }
}

export default useJoinSession
